import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import {
    Typography,
    Sheet,
    Card,
    CardContent,
    Grid,
    Box,
    Select,
    Option,
    Stack,
    Alert,
    CircularProgress,
    IconButton,
    Table,
    Chip,
    Divider,
    Button,
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Input,
    FormControl,
    FormLabel,
    Dropdown,
    MenuButton,
    Menu,
    MenuItem,
    ListItemDecorator,
} from '@mui/joy';
import {
    ArrowBack,
    AdminPanelSettings,
    Person,
    Security,
    Save,
    Add,
    Close,
    MoreVert,
    Delete,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_ADMIN_API_BASE || 'http://localhost:9300';

const PermissionsPage = () => {
    const { session, profile } = useAuth();
    const navigate = useNavigate();
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState({});
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [newAdminEmail, setNewAdminEmail] = useState('');
    const [addingAdmin, setAddingAdmin] = useState(false);
    const [addError, setAddError] = useState('');
    const [addSuccess, setAddSuccess] = useState('');
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [adminToDelete, setAdminToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deleteSuccess, setDeleteSuccess] = useState('');

    const loadAdmins = async () => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`${API_BASE}/profiles`, {
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            setAdmins(data);
        } catch (err) {
            console.error('Failed to load admin profiles:', err);
            setError('Failed to load admin profiles. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAdmins();
    }, []);

    const handlePermissionChange = async (adminId, scope, newValue) => {
        try {
            setSaving(prev => ({ ...prev, [`${adminId}-${scope}`]: true }));

            // Update the local state immediately for better UX
            setAdmins(prev => prev.map(admin => {
                if (admin.id === adminId) {
                    return {
                        ...admin,
                        permissions: admin.permissions.map(perm => 
                            perm.scope === scope ? { ...perm, value: newValue } : perm
                        )
                    };
                }
                return admin;
            }));

            // Make API call to update permission
            const response = await fetch(`${API_BASE}/profiles/${adminId}/permissions`, {
                method: 'PATCH',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ scope, value: newValue })
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to update permission: ${response.status} ${errorData}`);
            }

            const result = await response.json();
            
            // Success - no need to update state again as it's already updated optimistically
            console.log('Permission updated successfully:', result);

        } catch (err) {
            console.error('Failed to update permission:', err);
            // Revert the local change on error
            loadAdmins();
            
            // Show error to user
            setError(`Failed to update permission: ${err.message}`);
            
            // Clear error after 5 seconds
            setTimeout(() => setError(null), 5000);
        } finally {
            setSaving(prev => ({ ...prev, [`${adminId}-${scope}`]: false }));
        }
    };

    const handleOpenDeleteModal = (admin) => {
        setAdminToDelete(admin);
        setDeleteModalOpen(true);
        setDeleteError('');
        setDeleteSuccess('');
    };

    const handleCloseDeleteModal = () => {
        setDeleteModalOpen(false);
        setAdminToDelete(null);
        setDeleteError('');
        setDeleteSuccess('');
        setDeleting(false);
    };

    const handleDeleteAdmin = async () => {
        if (!adminToDelete) return;

        try {
            setDeleting(true);
            setDeleteError('');
            setDeleteSuccess('');

            const response = await fetch(`${API_BASE}/profiles/${adminToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-ID': session?.vendorId,
                }
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to delete admin: ${response.status} ${errorData}`);
            }

            setDeleteSuccess(`Admin ${adminToDelete.name || adminToDelete.email} deleted successfully!`);
            
            // Remove the deleted admin from the list
            setAdmins(prev => prev.filter(admin => admin.id !== adminToDelete.id));
            
            // Close modal after short delay
            setTimeout(() => {
                handleCloseDeleteModal();
            }, 1500);

        } catch (err) {
            console.error('Failed to delete admin:', err);
            setDeleteError(err.message || 'Failed to delete admin. Please try again.');
        } finally {
            setDeleting(false);
        }
    };

    const getPermissionColor = (value) => {
        switch (value) {
            case 'write': return 'success';
            case 'read': return 'warning';
            case 'none': return 'neutral';
            default: return 'neutral';
        }
    };

    const formatDate = (timestamp) => {
        return new Date(timestamp).toLocaleDateString();
    };

    const handleAddAdmin = async () => {
        if (!newAdminEmail.trim()) {
            setAddError('Please enter an email address');
            return;
        }

        try {
            setAddingAdmin(true);
            setAddError('');
            setAddSuccess('');

            const response = await fetch(`${API_BASE}/profiles`, {
                method: 'POST',
                headers: {
                    'Authorization': session?.accessToken,
                    'Vendor-Id': session?.vendorId,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    email: newAdminEmail.trim()
                })
            });

            if (!response.ok) {
                const errorData = await response.text();
                throw new Error(`Failed to add admin: ${response.status} ${errorData}`);
            }

            const newAdmin = await response.json();
            setAddSuccess(`Admin ${newAdminEmail} added successfully!`);
            
            // Reset form
            setNewAdminEmail('');
            
            // Reload admins list
            await loadAdmins();
            
            // Close modal after short delay
            setTimeout(() => {
                setAddModalOpen(false);
                setAddSuccess('');
            }, 1500);

        } catch (err) {
            console.error('Failed to add admin:', err);
            setAddError(err.message || 'Failed to add admin. Please try again.');
        } finally {
            setAddingAdmin(false);
        }
    };

    const handleCloseAddModal = () => {
        setAddModalOpen(false);
        setNewAdminEmail('');
        setAddError('');
        setAddSuccess('');
    };

    if (loading) {
        return (
            <Sheet sx={{ p: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                <Stack spacing={2} alignItems="center">
                    <CircularProgress size="lg" />
                    <Typography level="body-lg">Loading admin profiles...</Typography>
                </Stack>
            </Sheet>
        );
    }

    if (error) {
        return (
            <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
                <Alert color="danger" variant="soft" sx={{ mb: 3 }}>
                    <Typography>{error}</Typography>
                </Alert>
                <Typography level="body-sm" textColor="neutral.500">
                    Please try refreshing the page or contact support if the problem persists.
                </Typography>
            </Sheet>
        );
    }

    return (
        <Sheet sx={{ p: 4, maxWidth: '1400px', mx: 'auto' }}>
            {/* Header */}
            <Box sx={{ mb: 4 }}>
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                    <IconButton 
                        variant="outlined" 
                        onClick={() => navigate('/admin')}
                        sx={{ borderRadius: '50%' }}
                    >
                        <ArrowBack />
                    </IconButton>
                    <AdminPanelSettings sx={{ fontSize: 32, color: 'primary.500' }} />
                    <Box sx={{ flex: 1 }}>
                        <Typography level="h2" sx={{ mb: 0.5 }}>
                            Admins & Permissions
                        </Typography>
                        <Typography level="body-sm" textColor="neutral.500">
                            Manage administrator accounts and access permissions
                        </Typography>
                    </Box>
                    <Button
                        startDecorator={<Add />}
                        onClick={() => setAddModalOpen(true)}
                        variant="solid"
                        color="primary"
                    >
                        Add Admin
                    </Button>
                </Stack>
            </Box>

            {/* Admins List */}
            <Box sx={{ mb: 3 }}>
                <Typography level="h4" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%', 
                        bgcolor: 'primary.100', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        mr: 2 
                    }}>
                        <Security sx={{ color: 'primary.600' }} />
                    </Box>
                    Administrator Accounts
                </Typography>
                
                {admins.length === 0 ? (
                    <Card variant="soft" sx={{ p: 4, textAlign: 'center' }}>
                        <Person sx={{ fontSize: 48, color: 'neutral.400', mb: 2 }} />
                        <Typography level="h4" sx={{ mb: 1 }}>No Administrators Found</Typography>
                        <Typography level="body-md" textColor="neutral.500">
                            No administrator accounts are currently configured.
                        </Typography>
                    </Card>
                ) : (
                    <Grid container spacing={4}>
                        {admins.map((admin) => (
                            <Grid key={admin.id} xs={12}>
                                <Card variant="outlined">
                                    <CardContent sx={{ p: 3 }}>
                                        <Stack spacing={3}>
                                            {/* Admin Header */}
                                            <Stack direction="row" alignItems="center" spacing={2}>
                                                <Box sx={{ 
                                                    width: 48, 
                                                    height: 48, 
                                                    borderRadius: '50%', 
                                                    bgcolor: 'primary.100', 
                                                    display: 'flex', 
                                                    alignItems: 'center', 
                                                    justifyContent: 'center' 
                                                }}>
                                                    <Person sx={{ color: 'primary.600', fontSize: 24 }} />
                                                </Box>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography level="h4" sx={{ mb: 0.5 }}>
                                                        {admin.name || 'Waiting for login'}
                                                    </Typography>
                                                    <Typography level="body-sm" textColor="neutral.500">
                                                        {admin.email}
                                                    </Typography>
                                                    {!admin.name && (
                                                        <Typography level="body-xs" textColor="warning.500" sx={{ mt: 0.5 }}>
                                                            Name will appear after first login
                                                        </Typography>
                                                    )}
                                                </Box>
                                                <Stack direction="row" spacing={2} alignItems="center">
                                                    <Stack spacing={1} alignItems="flex-end">
                                                        <Typography level="body-xs" textColor="neutral.500">
                                                            Member since: {formatDate(admin.created_at)}
                                                        </Typography>
                                                        <Chip size="sm" variant="soft" color="primary">
                                                            ID: {admin.id}
                                                        </Chip>
                                                    </Stack>
                                                    
                                                    {/* Three-dots menu - only show for other admins, not current user */}
                                                    {profile?.id !== admin.id && (
                                                        <Dropdown>
                                                            <MenuButton
                                                                slots={{ root: IconButton }}
                                                                slotProps={{ root: { variant: 'outlined', size: 'sm' } }}
                                                            >
                                                                <MoreVert />
                                                            </MenuButton>
                                                            <Menu>
                                                                <MenuItem
                                                                    onClick={() => handleOpenDeleteModal(admin)}
                                                                    sx={{ color: 'danger.500' }}
                                                                >
                                                                    <ListItemDecorator>
                                                                        <Delete />
                                                                    </ListItemDecorator>
                                                                    Delete
                                                                </MenuItem>
                                                            </Menu>
                                                        </Dropdown>
                                                    )}
                                                </Stack>
                                            </Stack>

                                            <Divider />

                                            {/* Permissions */}
                                            <Box>
                                                <Typography level="title-md" sx={{ mb: 2 }}>
                                                    Permissions
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {admin.permissions.map((permission) => {
                                                        const nameParts = permission.name.split(' ');
                                                        const firstLine = nameParts[0] || '';
                                                        const secondLine = nameParts.slice(1).join(' ') || '';
                                                        
                                                        return (
                                                            <Grid key={permission.scope} xs={12} sm={6} md={2}>
                                                                <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                                                                    <Stack spacing={1.5}>
                                                                        {/* Permission Header */}
                                                                        <Box sx={{ mb: 1 }}>
                                                                            <Typography level="title-sm" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                                                                                {firstLine}
                                                                            </Typography>
                                                                            <Typography level="title-sm" sx={{ lineHeight: 1.2, fontWeight: 'bold' }}>
                                                                                {secondLine}
                                                                            </Typography>
                                                                        </Box>
                                                                        
                                                                        {/* Vertical Button Group */}
                                                                        <Stack spacing={0.5}>
                                                                            {['none', 'read', 'write'].map((value) => (
                                                                                <Box 
                                                                                    key={value}
                                                                                    onClick={() => handlePermissionChange(admin.id, permission.scope, value)}
                                                                                    sx={{
                                                                                        display: 'flex',
                                                                                        alignItems: 'center',
                                                                                        justifyContent: 'center',
                                                                                        p: 0.75,
                                                                                        borderRadius: 'sm',
                                                                                        cursor: 'pointer',
                                                                                        backgroundColor: permission.value === value ? `${getPermissionColor(value)}.100` : 'transparent',
                                                                                        border: '1px solid',
                                                                                        borderColor: permission.value === value ? `${getPermissionColor(value)}.300` : 'neutral.200',
                                                                                        opacity: saving[`${admin.id}-${permission.scope}`] ? 0.6 : 1,
                                                                                        pointerEvents: saving[`${admin.id}-${permission.scope}`] ? 'none' : 'auto',
                                                                                        '&:hover': {
                                                                                            backgroundColor: permission.value === value ? `${getPermissionColor(value)}.150` : 'neutral.50'
                                                                                        }
                                                                                    }}
                                                                                >
                                                                                    <Typography 
                                                                                        level="body-sm" 
                                                                                        sx={{ 
                                                                                            textTransform: 'capitalize',
                                                                                            fontWeight: permission.value === value ? 'bold' : 'normal',
                                                                                            color: permission.value === value ? `${getPermissionColor(value)}.700` : 'neutral.600'
                                                                                        }}
                                                                                    >
                                                                                        {saving[`${admin.id}-${permission.scope}`] && permission.value === value ? (
                                                                                            <CircularProgress size="sm" />
                                                                                        ) : (
                                                                                            value
                                                                                        )}
                                                                                    </Typography>
                                                                                </Box>
                                                                            ))}
                                                                        </Stack>
                                                                    </Stack>
                                                                </Card>
                                                            </Grid>
                                                        );
                                                    })}
                                                </Grid>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                )}
            </Box>

            {/* Add Admin Modal */}
            <Modal open={addModalOpen} onClose={handleCloseAddModal}>
                <ModalDialog sx={{ minWidth: 400 }}>
                    <DialogTitle>
                        Add New Administrator
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseAddModal}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Typography level="body-md">
                                Enter the email address of the new administrator. They will receive access 
                                after their first login with Google.
                            </Typography>
                            
                            <FormControl>
                                <FormLabel>Email Address</FormLabel>
                                <Input
                                    type="email"
                                    placeholder="admin@example.com"
                                    value={newAdminEmail}
                                    onChange={(e) => setNewAdminEmail(e.target.value)}
                                    disabled={addingAdmin}
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter' && !addingAdmin) {
                                            handleAddAdmin();
                                        }
                                    }}
                                />
                            </FormControl>

                            {addError && (
                                <Alert color="danger" variant="soft">
                                    {addError}
                                </Alert>
                            )}

                            {addSuccess && (
                                <Alert color="success" variant="soft">
                                    {addSuccess}
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={handleCloseAddModal}
                            disabled={addingAdmin}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            onClick={handleAddAdmin}
                            loading={addingAdmin}
                            disabled={!newAdminEmail.trim() || addingAdmin}
                            startDecorator={!addingAdmin ? <Add /> : null}
                        >
                            {addingAdmin ? 'Adding...' : 'Add Admin'}
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal open={deleteModalOpen} onClose={handleCloseDeleteModal}>
                <ModalDialog sx={{ minWidth: 400 }}>
                    <DialogTitle>
                        Confirm Delete Admin
                        <IconButton
                            aria-label="close"
                            onClick={handleCloseDeleteModal}
                            sx={{ position: 'absolute', right: 8, top: 8 }}
                        >
                            <Close />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent>
                        <Stack spacing={2}>
                            <Typography level="body-md">
                                Are you sure you want to delete this administrator account?
                            </Typography>
                            
                            {adminToDelete && (
                                <Card variant="soft" sx={{ p: 2 }}>
                                    <Stack spacing={1}>
                                        <Typography level="title-md" fontWeight="bold">
                                            {adminToDelete.name || 'Waiting for login'}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            Email: {adminToDelete.email}
                                        </Typography>
                                        <Typography level="body-sm" textColor="neutral.600">
                                            ID: {adminToDelete.id}
                                        </Typography>
                                    </Stack>
                                </Card>
                            )}

                            <Alert color="danger" variant="soft">
                                <Typography level="body-sm">
                                    <strong>Warning:</strong> Admin will lose access to admin panel immediately. It can be undone only via admin re-creation.
                                </Typography>
                            </Alert>

                            {deleteError && (
                                <Alert color="danger" variant="soft">
                                    {deleteError}
                                </Alert>
                            )}

                            {deleteSuccess && (
                                <Alert color="success" variant="soft">
                                    {deleteSuccess}
                                </Alert>
                            )}
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            variant="outlined"
                            onClick={handleCloseDeleteModal}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="solid"
                            color="danger"
                            onClick={handleDeleteAdmin}
                            loading={deleting}
                            disabled={deleting}
                            startDecorator={!deleting ? <Delete /> : null}
                        >
                            {deleting ? 'Deleting...' : 'Delete Admin'}
                        </Button>
                    </DialogActions>
                </ModalDialog>
            </Modal>
        </Sheet>
    );
};

export default PermissionsPage; 